import {
  Button,
  Checkbox,
  Input,
  Kbd,
  Select,
  SelectItem,
} from "@heroui/react";
import React from "react";
import { isMacOs, isIOS } from "react-device-detect";
import { useTranslation } from "react-i18next";

/**
 * @typedef {object} Team
 * @property {string} id The team id
 * @property {string} name The team name
 * @property {boolean} isPrimary Whether this is the user's primary team
 */

/**
 * @param {object} props The component props
 * @param {React.RefObject<React.ReactElement>} props.inputRef - Ref for the input element
 * @param {number} props.index The index of the input if in a list.
 * @param {boolean} props.isLast Whether this is the last element in the list
 * @param {boolean} props.isFinal Whether this is the final list item
 * @param {string} props.placeholderText A default input string
 * @param {string} props.valueText Actual data for the input
 * @param {boolean} props.checked Whether the input is checked or not
 * @param {string} [props.teamId] The currently selected team id
 * @param {Team[]} [props.teams] Available teams for selection
 * @param {boolean} [props.isLocked] Whether the goals are locked (only checkbox editable)
 * @param {(index: number) => void} props.onEnter Callback when the Enter key is pressed
 * @param {(checked: boolean, value: string, index: number) => void} props.onChange Callback when the value changes key
 * @param {(index: number, isShiftKey: boolean) => void} props.onTab Callback when the Tab key is pressed
 * @param {(teamId: string, index: number) => void} [props.onTeamChange] Callback when team selection changes
 * @param {(index: number) => void} [props.onDelete] Callback when the delete button is clicked
 * @returns {React.ReactElement} An input with a checkbox
 */
export default function CheckboxInput({
  inputRef,
  index,
  isLast,
  isFinal,
  placeholderText = "",
  valueText = "",
  checked = false,
  teamId = null,
  teams = [],
  isLocked = false,
  onEnter = (_index) => {},
  onChange = (_checked, _value, _index) => {},
  onTab = (_index, _isShiftKey) => {},
  onTeamChange = (_teamId, _index) => {},
  onDelete,
}) {
  const { t } = useTranslation();
  const [isSelected, setIsSelected] = React.useState(checked);
  const [value, setValue] = React.useState(valueText);

  const enterKeyName = isMacOs || isIOS ? t("keyReturn") : t("keyEnter");

  React.useEffect(() => {
    setIsSelected(checked);
    setValue(valueText);
  }, [checked, valueText]);

  const checkboxRef = React.useRef(null);

  React.useEffect(() => {
    if (checkboxRef.current) {
      const checkboxElement = checkboxRef.current.querySelector(
        'input[type="checkbox"]',
      );
      if (checkboxElement) {
        checkboxElement.tabIndex = -1;
      }
    }
  }, []);

  const showTeamSelector = teams.length > 1 && !isLocked;

  return (
    <div className="flex w-full flex-col gap-1">
      {showTeamSelector && (
        <div className="ml-8">
          <Select
            size="sm"
            variant="flat"
            aria-label={t("goals.selectTeam")}
            selectedKeys={teamId ? [teamId] : []}
            onSelectionChange={(keys) => {
              const selectedId = Array.from(keys)[0];
              if (selectedId) {
                onTeamChange(selectedId, index);
              }
            }}
            className="max-w-[200px]"
          >
            {teams.map((team) => (
              <SelectItem key={team.id} textValue={team.name}>
                {team.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}
      <div className="flex w-full gap-2">
        <div ref={checkboxRef} className="flex items-center">
          <Checkbox
            isSelected={isSelected}
            onValueChange={(newCheck) => {
              setIsSelected(newCheck);
              onChange(newCheck, value, index);
            }}
            size="lg"
          />
        </div>
        <div className="relative grow">
          {isLocked ? (
            <div
              className={`flex min-h-[48px] items-center rounded-lg px-3 py-2 text-lg font-semibold ${
                isSelected ? "text-default-400 line-through" : ""
              }`}
            >
              {value || (
                <span className="text-default-400 italic">
                  {placeholderText}
                </span>
              )}
            </div>
          ) : (
            <>
              <Input
                ref={inputRef}
                type="text"
                size="lg"
                radius="sm"
                variant="bordered"
                placeholder={placeholderText}
                value={value}
                onValueChange={(newValue) => {
                  setValue(newValue);
                  onChange(isSelected, newValue, index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value.length > 0) {
                      onEnter(index);
                    }
                  } else if (e.key === "Tab") {
                    onTab(index, e.shiftKey);
                  }
                }}
              />
              {valueText && isLast && !isFinal && (
                <div className="absolute -bottom-2 -right-2">
                  <Kbd keys={["enter"]}>
                    {t("hitEnter", { keyName: enterKeyName })}
                  </Kbd>
                </div>
              )}
            </>
          )}
        </div>
        {!isLocked && value && onDelete && (
          <div className="flex items-center">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              aria-label={t("goals.deleteGoal")}
              onPress={() => onDelete(index)}
            >
              ✕
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
